// src/pages/Private/Teacher/CourseManagement/ModuleLessons.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../../utils/axios';
import './ModuleLessons.css';

function ModuleLessons() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [newLesson, setNewLesson] = useState({
    title: '',
    description: '',
    content: { videoUrl: '', pdfUrl: '', resources: [] },
    zoomLink: '',
    scheduledAt: '',
    durationMinutes: 60,
    duration: 60,
    order: 1,
    isFree: false
  });

  useEffect(() => {
    loadModuleData();
  }, [moduleId]);

  const loadModuleData = async () => {
    try {
      const [moduleRes, lessonsRes] = await Promise.all([
        axios.get(`/modules/${moduleId}`),
        axios.get(`/lessons/module/${moduleId}`)
      ]);
      setModule(moduleRes.data);
      setLessons(lessonsRes.data);
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/lessons', {
        moduleId,
        ...newLesson,
        order: lessons.length + 1
      });
      setLessons([...lessons, response.data]);
      setShowAddLesson(false);
      setNewLesson({
        title: '',
        description: '',
        content: { videoUrl: '', pdfUrl: '', resources: [] },
        zoomLink: '',
        scheduledAt: '',
        durationMinutes: 60,
        duration: 60,
        isFree: false
      });
    } catch (error) {
      console.error('Ders eklenirken hata:', error);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Bu dersi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axios.delete(`/lessons/${lessonId}`);
      setLessons(lessons.filter(l => l._id !== lessonId));
    } catch (error) {
      console.error('Ders silinirken hata:', error);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  if (loading) return <div className="loading">Yükleniyor...</div>;

  return (
    <div className="module-lessons">
      <div className="lessons-header">
        <div>
          <h1>{module?.title} - Dersler</h1>
          <button className="btn-back" onClick={() => navigate(-1)}>← Geri</button>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowAddLesson(true)}
        >
          + Yeni Ders
        </button>
      </div>

      {showAddLesson && (
        <div className="modal">
          <div className="modal-content">
            <h3>Yeni Ders Ekle</h3>
            <form onSubmit={handleAddLesson}>
              <div className="form-group">
                <label>Ders Başlığı *</label>
                <input
                  type="text"
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={newLesson.description}
                  onChange={(e) => setNewLesson({...newLesson, description: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Video URL</label>
                  <input
                    type="url"
                    value={newLesson.content.videoUrl}
                    onChange={(e) => setNewLesson({
                      ...newLesson,
                      content: {...newLesson.content, videoUrl: e.target.value}
                    })}
                    placeholder="https://..."
                  />
                </div>

                <div className="form-group">
                  <label>PDF URL</label>
                  <input
                    type="url"
                    value={newLesson.content.pdfUrl}
                    onChange={(e) => setNewLesson({
                      ...newLesson,
                      content: {...newLesson.content, pdfUrl: e.target.value}
                    })}
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zoom Linki</label>
                  <input
                    type="url"
                    value={newLesson.zoomLink}
                    onChange={(e) => setNewLesson({...newLesson, zoomLink: e.target.value})}
                    placeholder="https://zoom.us/j/..."
                  />
                </div>

                <div className="form-group">
                  <label>Ders Tarihi</label>
                  <input
                    type="datetime-local"
                    value={newLesson.scheduledAt}
                    onChange={(e) => setNewLesson({...newLesson, scheduledAt: e.target.value})}
                    min={getMinDateTime()}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Süre (dakika)</label>
                  <select
                    value={newLesson.durationMinutes}
                    onChange={(e) => setNewLesson({
                      ...newLesson, 
                      durationMinutes: parseInt(e.target.value),
                      duration: parseInt(e.target.value)
                    })}
                  >
                    <option value="30">30 dakika</option>
                    <option value="45">45 dakika</option>
                    <option value="60">60 dakika</option>
                    <option value="90">90 dakika</option>
                    <option value="120">120 dakika</option>
                  </select>
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={newLesson.isFree}
                      onChange={(e) => setNewLesson({...newLesson, isFree: e.target.checked})}
                    />
                    Ücretsiz Önizleme
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-success">Kaydet</button>
                <button type="button" className="btn-cancel" onClick={() => setShowAddLesson(false)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="lessons-list">
        {lessons.length === 0 ? (
          <div className="empty-state">
            <p>Henüz ders eklenmemiş. İlk dersi ekleyin!</p>
          </div>
        ) : (
          lessons.map((lesson, index) => (
            <div key={lesson._id} className="lesson-card">
              <div className="lesson-header">
                <div>
                  <h3>{index + 1}. {lesson.title}</h3>
                  <p>{lesson.description}</p>
                </div>
                <div className="lesson-badges">
                  {lesson.isFree && <span className="badge-free">Ücretsiz</span>}
                  <span className="badge-duration">{lesson.duration || lesson.durationMinutes} dk</span>
                  {lesson.scheduledAt && (
                    <span className="badge-date">
                      {new Date(lesson.scheduledAt).toLocaleDateString('tr-TR')}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="lesson-content-preview">
                {lesson.content?.videoUrl && (
                  <a href={lesson.content.videoUrl} target="_blank" rel="noopener noreferrer" className="video-link">
                    🎥 Video
                  </a>
                )}
                {lesson.content?.pdfUrl && (
                  <a href={lesson.content.pdfUrl} target="_blank" rel="noopener noreferrer" className="pdf-link">
                    📄 PDF
                  </a>
                )}
                {lesson.zoomLink && (
                  <a href={lesson.zoomLink} target="_blank" rel="noopener noreferrer" className="zoom-link">
                    🔗 Zoom
                  </a>
                )}
              </div>

              <div className="lesson-actions">
                <button
                  className="btn-quiz"
                  onClick={() => navigate(`/teacher/lesson/${lesson._id}/create-quiz`)}
                >
                  📝 Quiz Ekle
                </button>
                <button
                  className="btn-assignment"
                  onClick={() => navigate(`/teacher/lesson/${lesson._id}/create-assignment`)}
                >
                  📋 Ödev Ekle
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteLesson(lesson._id)}
                >
                  🗑️ Sil
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ModuleLessons;